import type { TemplateDoc } from '@quickreport/domain';
import { Field, Input } from '@quickreport/ui';
import { useEffect, useState } from 'react';
import { useWorkspace } from '../../lib/workspace';

const DEPARTMENTS = [
  'Haematology',
  'Biochemistry',
  'Immunology',
  'Clinical Pathology',
  'Serology',
  'Microbiology',
  'Histopathology',
  'Molecular',
];

/** Template-level settings shown when no row is selected. */
export function TemplatePanel({
  doc,
  onChange,
}: {
  doc: TemplateDoc;
  onChange: (fn: (d: TemplateDoc) => TemplateDoc) => void;
}) {
  const { can } = useWorkspace();
  const set = (patch: Partial<TemplateDoc>) => onChange((d) => ({ ...d, ...patch }));
  const setPrint = (patch: Partial<TemplateDoc['print']>) =>
    onChange((d) => ({ ...d, print: { ...d.print, ...patch } }));
  return (
    <aside
      className="grid content-start items-start gap-4 p-4 md:grid-cols-2 xl:grid-cols-4"
      aria-label="Template settings"
    >
      <h2 className="col-span-full text-[15px] font-semibold">Template settings</h2>
      <Field label="Other names people search for" hint="Separate with commas, e.g. Hemogram, CBC">
        <Input
          value={doc.aliases.join(', ')}
          onChange={(e) =>
            set({
              aliases: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      </Field>
      <Field label="Department">
        <Input
          list="departments"
          value={doc.department ?? ''}
          onChange={(e) => set({ department: e.target.value || undefined })}
        />
        <datalist id="departments">
          {DEPARTMENTS.map((d) => (
            <option key={d} value={d} />
          ))}
        </datalist>
      </Field>
      <div className="contents">
        <Field label="Sample type">
          <Input
            value={doc.sampleType ?? ''}
            onChange={(e) => set({ sampleType: e.target.value || undefined })}
          />
        </Field>
        <Field label="Instrument">
          <Input
            value={doc.instrument ?? ''}
            onChange={(e) => set({ instrument: e.target.value || undefined })}
          />
        </Field>
      </div>
      <Field
        label="Report ready in (days)"
        hint="0 or empty = the same day. Sets the due date when a report is made."
      >
        <Input
          inputMode="numeric"
          value={doc.turnaroundDays === undefined ? '' : String(doc.turnaroundDays)}
          placeholder="0"
          onChange={(e) => {
            const t = e.target.value.replace(/\D/g, '').slice(0, 2);
            set({ turnaroundDays: t === '' ? undefined : Math.min(60, Number(t)) });
          }}
        />
      </Field>
      {can('view_billing') ? (
        <PriceField value={doc.price} onChange={(price) => set({ price })} />
      ) : null}
      <Field label="Interpretation printed under the table" className="md:col-span-2">
        <textarea
          value={doc.interpretation ?? ''}
          onChange={(e) => set({ interpretation: e.target.value || undefined })}
          rows={4}
          className="w-full rounded-md border border-line p-2 text-[13px] focus:border-brand focus:outline-none"
        />
      </Field>
      <div className="col-span-full grid gap-2 text-[13px] md:grid-cols-2">
        <Toggle
          label="Sensitive test"
          hint="Never listed in a patient's report list or summarised in messages (HIV, pregnancy, semen analysis)."
          checked={doc.sensitive}
          onChange={(v) => set({ sensitive: v })}
        />
        <Toggle
          label="Always a separate report"
          hint="For slow tests such as cultures, so they don't hold up the rest of the visit."
          checked={doc.separateReport}
          onChange={(v) => set({ separateReport: v })}
        />
        <Toggle
          label="Print Min and Max as one Reference Range column"
          hint="Reads better and handles one-sided ranges such as < 40."
          checked={doc.print.mergeRefColumns}
          onChange={(v) => setPrint({ mergeRefColumns: v })}
        />
        <Toggle
          label="Start on a new page"
          checked={doc.print.newPage}
          onChange={(v) => setPrint({ newPage: v })}
        />
      </div>
    </aside>
  );
}

/** Keeps what is typed (such as "99.") while the saved price is the number it means. */
function PriceField({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value));
  // A change that did not come from typing here (another version loaded) replaces the text.
  useEffect(() => {
    setText((t) =>
      Number(t) === value || (t === '' && value === undefined) ? t : String(value ?? ''),
    );
  }, [value]);
  return (
    <Field
      label="Price (₹)"
      hint="Private to the lab, never printed. A report keeps the price from when the test was chosen."
    >
      <Input
        inputMode="decimal"
        value={text}
        placeholder="0"
        onChange={(e) => {
          const t = e.target.value.replace(/[^\d.]/g, '');
          setText(t);
          const n = Number(t);
          onChange(t === '' || Number.isNaN(n) ? undefined : Math.round(n * 100) / 100);
        }}
      />
    </Field>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 accent-brand"
      />
      <span>
        <span className="font-medium">{label}</span>
        {hint ? <span className="block text-[12px] text-slate">{hint}</span> : null}
      </span>
    </label>
  );
}
